import { classInstance } from '@/shared/helpers/helpers';

import { FormObserver } from './composition/observer';
import { useValidate } from './composition/validate';

import { FormInput } from './form-element/form-input/form-input';
import { FormPassword } from './form-element/form-input/form-password';
import { FormSelect } from './form-element/form-select/form-select';
import { FormComplete } from './form-element/form-complete/form-complete';
import { FormTextArea } from './form-element/form-textarea/form-textarea';
import { FormCheckbox } from './form-element/form-checkbox/form-checkbox';
import { FormRadio } from './form-element/form-radio/form-radio';
import { FormFile } from './form-element/form-file/form-file';
import { FormCode } from './form-element/form-code/form-code';
import { FormMessage } from '@/shared/form/form-message/form-message';

/**
 * @category 3 Form
 * @desc
 * Список коллбэков для событий. Именуются по схеме on<Событие>, например, onSubmit, onReset.
 */

export interface Callbacks {
	/**
	 * Сообщение об ошибке
	 * @category 3 Form
	 * @typedef {Object} ErrorMessage
	 * @property {string} message Сообщение об ошибке.
	 * @property {string} code Код ошибки.
	 * @property {string} customData Дополнительные данные.
	 * @example
	 * {
	 * 	"message": "Ошибка",
	 * 	"code": "invalid_http_method",
	 * 	"customData": null
	 * }
	 */

	/**
	 * Результат неудачного выполнения
	 * @category 3 Form
	 * @typedef {Object} CallbackError
	 * @property {string} status Статус выполнения.
	 * @property {object} data Дополнительные данные, возвращённые с сервера.
	 * @property {Array<FormMessage>} errors Массив ошибок.
	 * @example
	 * {
	 * 	"status": "error",
	 * 	"data": null,
	 * 	"errors": [
	 * 		{
	 * 			"message": "Wrong method for current action",
	 * 			"code": "invalid_http_method",
	 * 			"customData": null
	 * 		}
	 * 	]
	 * }
	 */

	/**
	 * Результат успешного выполнения
	 * @category 3 Form
	 * @typedef {Object} CallbackResult
	 * @property {string} status Статус выполнения.
	 * @property {object} data Дополнительные данные, возвращённые с сервера.
	 * @example
	 * {
	 * 	"status": "success",
	 * 	"data": {
	 * 		"tel": "+7 (900) 010 10 10"
	 * 	}
	 * }
	 */

	/**
	 * Коллбэк, выполняющийся при отправке формы. Возвращает Promise.
	 * @category 3 Form
	 * @return {Promise<CallbackResult>} Отдаёт объект с успешным статусом и, опционально, дополнительными данными
	 * @throws {Promise<CallbackError>} Отдаёт объект с неудачным статусом, списком ошибок и, опционально, дополнительными данными
	 */
	onSubmit?: (formData: FormData) => Promise<object>,
}

/**
 * @desc UI Компонент Form
 * @category 3 Form
 * @example
 * document.querySelectorAll('.js-form').forEach((element) => {new Form(element)});
 * @constructor
 * @param {HTMLFormElement} selector HTML-элемент <form>
 * @param {Callbacks} callbacks объект типа Callbacks
 */

export class Form {
	readonly $form: HTMLFormElement;

	public formValid: boolean;
	protected formObserver: any;
	protected $buttons: Array<HTMLButtonElement>;
	private observer: MutationObserver;
	private callbacks: Callbacks;
	private messageElement: HTMLElement;
	private message: FormMessage;
	private errorMessage: string = '';

	constructor(selector: HTMLFormElement, callbacks: object) {
		this.$form = selector;

		if (!this.$form) return;
		this.callbacks = callbacks || {};

		this.init();
	}

	/**
	 * @desc Инициализировать компонент
	 */
	async init() {
		classInstance.set(this.$form, { form: this });

		this.$buttons = Array.from(this.$form.querySelectorAll('[type="submit"]'));

		this.formObserver = new FormObserver(this.$form);
		this.$form.querySelectorAll('input').forEach(($element: HTMLInputElement) => {
			const type = $element.dataset.type;
			switch (type) {
				case 'form-code':
					new FormCode($element);
					break;

				case 'form-file':
					new FormFile($element);
					break;

				case 'form-checkbox':
					new FormCheckbox($element);
					break;

				case 'form-radio':
					new FormRadio($element);
					break;

				case 'form-complete':
					new FormComplete($element);
					break;

				case 'form-password':
					new FormPassword($element);
					break;

				default:
					new FormInput($element);
					break;
			}
		});
		this.$form.querySelectorAll('select').forEach(($element: HTMLSelectElement) => {
			new FormSelect($element);
		});
		this.$form.querySelectorAll('textarea').forEach(($element: HTMLTextAreaElement) => {
			new FormTextArea($element);
		});

		this.checkHandler = this.checkHandler.bind(this);
		this.$form.addEventListener('formChange', this.checkHandler);

		this.formCheck(true);

		this.observer = new MutationObserver(this.handleMutation);
		this.observer.observe(this.$form, { childList: true, subtree: true });
		this.messageElement = this.$form.querySelector('.form-message');
		this.message = new FormMessage(this.messageElement);

		this.$form.addEventListener('submit', this.handleSubmit);
		this.$form.dispatchEvent(new CustomEvent('formInit'));
	}

	/**
	 * @desc Удалить обработчики событий
	 */
	public destroy(): void {
		classInstance.del(this.$form, 'form');

		[...this.$form.elements].forEach((element) => classInstance.get(element)?.formElement?.destroy());

		this.formObserver.destroy();
		this.$form.removeEventListener('formChange', this.checkHandler);

		this.observer.disconnect();
		this.observer = null;
		this.$form.dispatchEvent(new CustomEvent('formDestroy'));
	}

	/**
	 * @desc Переопределить обработчики событий
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-form'));
	 * myForm.form.reinit();
	 */
	public reinit(): void {
		this.destroy();
		this.init();
	}

	/**
	 * @desc Проверить валидность формы
	 * @param {Boolean} [noRender=false] - если указать true - представление не будет обновляться при валидации.
	 * @returns {Boolean} результат валидации
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-form'));
	 * myForm.form.formCheck();
	 */
	public formCheck(noRender?: boolean): boolean {
		const result: ResultVF = useValidate(this.$form, noRender);

		this.formValid = result.valid;
		this.render(result.looked);

		return result.valid;
	}

	private checkHandler(event: CustomEvent) {
		this.render(event.detail.looked);
	}

	private render(looked: boolean): void {
		this.$buttons.forEach((button) => (button.disabled = looked));
	}

	private handleMutation = (mutationList): void => {
		mutationList.forEach((mutation): void => {
			const selector = ':is(select, input, textarea)[data-look="true"]';
			const fieldAdded = mutation.type === 'childList' && [...mutation.addedNodes].find((node) => node.querySelector && node.querySelector(selector));
			if (fieldAdded) this.reinit();
		});
	};

	/**
	 * Событие успешной отправка формы.
	 * @category 3 Form
	 * @event Form#formSubmit
	 * @property {Object} detail.data данные, полученные с сервера в свойстве data.
	 * @property {Object} detail.formData данные формы в формате FormData.
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-form'));
	 * myForm.addEventListener('formSubmit', (event) => console.log(event.detail));
	 */
	private formSubmitEvent = (data: object): CustomEvent => new CustomEvent('formSubmit', this.formSubmitData(data));

	/**
	 * @description Отображение сообщения.
	 * @param {string} message Текст сообщения.
	 * @param {string} type Тип сообщения: error — ошибка, warning — предупреждение, success — успех.
	 * @example
	 * const message = app.classInstance.get(document.querySelector('.js-form'));
	 * myForm.form.set('Ошибка', 'error');
	 */
	public setMessage = (message: string | null, type?: string | null): void => {
		this.errorMessage = message || this.errorMessage;
		this.message.set(this.errorMessage, type);
	};

	/**
	 * @description Скрытие общей ошибки.
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-form'));
	 * myForm.form.removeError();
	 */
	public removeMessage = (): void => this.message.disable();

	/**
	 * @desc Добавление коллбэков после создания формы.
	 * @param {Callbacks} callbacks объект типа Callbacks.
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-form'));
	 * myForm.form.addCallbacks({ onSubmit });
	 */
	public addCallbacks = (callbacks: Callbacks): void => {
		this.callbacks = { ...this.callbacks, ...callbacks };
	};

	/**
	 * Настройки события formSubmit.
	 * @typedef {Object} FormSubmitOptions
	 * @property {boolean} bubbles true
	 * @property {object} detail.formData данные формы.
	 * @property {object} detail.data данные, пришедшие с сервера.
	 */

	/**
	 * Возвращает настройки для события успешной отправки.
	 * @category 3 Form
	 * @returns {FormSubmitOptions}
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-form'));
	 * myForm.addEventListener('formSubmit', (event) => console.log(event.detail));
	 */
	private formSubmitData = (data?: object): object => ({
		bubbles: true,
		detail: {
			formData: new FormData(this.$form),
			data,
		},
	});

	/**
	 * Обработчик события submit. Вызывает коллбэк onSubmit, если он есть, и в зависимости от его результата:
	 * 
	 * * при успехе генерирует событие formSubmit;  
	 * * при ошибке отображает полученные сообщения.
	 * 
	 * Дополнительную логику при успехе надо привязывать к событию formSubmit.
	 */
	private handleSubmit = (event: Event): void => {
		if (this.callbacks.onSubmit) {
			event.preventDefault();
			this.callbacks.onSubmit(new FormData(this.$form))
				.then(
					(result) => (result.json ? result.json() : result),
					(error) => (error?.json ? error.json() : error),
				)
				.then((result) => {
					this.removeMessage();
					if (result.status === 'success') {
						this.$form.dispatchEvent(this.formSubmitEvent(result.data));
						if (result.data?.message) this.setMessage(result.data.message, 'success');
					} else this.setMessage(result.errors.map((err) => err.message).join('<br>'), 'error');
				});
		}
	};
}
